<template>
  <div class="quantity production">
    <!-- Display principal -->
    <div class="displayCalc">
      <div class="number">
        <a>{{ quantityStr }}</a>
      </div>
    </div>

    <!-- Números 1-9 -->
    <div class="row" v-for="(row, rowIndex) in numberRows" :key="rowIndex">
      <div
        class="column"
        v-for="num in row"
        :key="num"
        @click="appendNumber(num)"
      >
        <div class="number">
          <a>{{ num }}</a>
        </div>
      </div>
    </div>

    <!-- Fila coma / 0 / DEL -->
    <div class="row">
      <div
        class="column comma"
        :class="{ disabled: !isKgMode || quantityHasDecimal }"
        @click="appendDecimal"
      >
        <div class="number"><a>,</a></div>
      </div>
      <div class="column zero" @click="appendNumber(0)">
        <div class="number"><a>0</a></div>
      </div>
      <div class="column del" @click="deleteDigit">
        <div class="number"><a>⌫</a></div>
      </div>
    </div>

    <!-- Fila de acción: AC / OK -->
    <div class="row">
      <div class="column ac" @click="resetQuantity">
        <div class="number"><a>AC</a></div>
      </div>
      <div class="column ok" @click="openDialog">
        <div class="number"><a>OK</a></div>
      </div>
    </div>

    <!-- Confirmación de guardado -->
    <dialog ref="dialog">
      <h2 class="dialog-title">Guardar producción</h2>
      <div class="dialog-content">
        <p>Se va a proceder a guardar los siguientes datos:</p>
        <p><strong>Finca:</strong> {{ farmland.name }}</p>
        <p v-if="zone">
          <strong>Zona:</strong> {{ zone.name }} ({{ zone.code }})
        </p>
        <p v-if="category"><strong>Categoría:</strong> {{ category }}</p>
        <p><strong>Formato:</strong> {{ formatLabel }}</p>
        <p><strong>Cantidad:</strong> {{ quantity }}</p>
        <div class="created-on-field">
          <label for="createdOnInput">
            <strong>Fecha y hora:</strong>
          </label>
          <input
            id="createdOnInput"
            v-model="createdOn"
            type="datetime-local"
            class="datetime-input"
          />
        </div>
      </div>
      <div class="icon-cantidad">
        <div class="icon-wrapper">
          <img
            :src="cancelIcon"
            alt="Cancelar"
            class="icon cancel"
            @click.prevent="closeDialog"
          />
          <p class="icon-label">Cancelar</p>
        </div>
        <div class="icon-wrapper">
          <img
            :src="okeyIcon"
            alt="Confirmar"
            class="icon okey"
            @click.prevent="confirmSave"
          />
          <p class="icon-label">Confirmar</p>
        </div>
      </div>
    </dialog>

    <!-- Resultado del guardado con ícono -->
    <dialog ref="resultDialog">
      <div class="result-dialog-content">
        <img
          :src="resultDialog.success ? successIcon : errorIcon"
          :alt="resultDialog.success ? 'Éxito' : 'Error'"
          class="result-icon"
        />
        <h2 class="dialog-title">{{ resultDialog.message }}</h2>
        <p
          v-if="resultDialog.warning === 'duplicate_same_day'"
          class="dialog-warning"
        >
          ⚠️ Ya existe otro registro hoy con la misma Finca, Zona y Categoría.
        </p>
        <div v-if="resultDialog.success" class="result-dialog-buttons">
          <button @click="acceptAndGoHome" class="result-button accept-button">
            Aceptar
          </button>
          <!-- <button @click="goToMode" class="result-button mode-button">
            Volver a Modo
          </button> -->
          <button @click="goToCategory" class="result-button category-button">
            Seguir contando
          </button>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script>
import errorIcon from "@/assets/icon/advertencia.png";
import cancelIcon from "@/assets/icon/cancel.png";
import successIcon from "@/assets/icon/exito.png";
import okeyIcon from "@/assets/icon/okey.png";
import { cacheDelete } from "@/services/offlineService";

export default {
  name: "QuantityWidget",

  props: {
    farmland: { type: Object, required: true },
    zone: { type: Object, required: false, default: null },
    format: { type: Object, required: true },
    category: { type: String, required: false, default: null },
    modoObj: { type: Object, required: false, default: null },
    categoriaObj: { type: Object, required: false, default: null },
  },

  data() {
    return {
      quantityStr: "0",
      maxLength: 7,
      cancelIcon,
      okeyIcon,
      successIcon,
      errorIcon,
      createdOn: "",
      resultDialog: {
        success: null,
        message: "",
        warning: null,
      },
    };
  },

  computed: {
    numberRows() {
      return [
        [7, 8, 9],
        [4, 5, 6],
        [1, 2, 3],
      ];
    },

    isKgMode() {
      return this.format && this.format.name?.toUpperCase().includes("KG");
    },

    formatLabel() {
      return this.format?.name || "Sin formato";
    },

    quantityHasDecimal() {
      return this.quantityStr.includes(",");
    },

    quantity() {
      return this.isKgMode
        ? parseFloat(this.quantityStr.replace(",", ".")) || 0
        : parseInt(this.quantityStr) || 0;
    },
  },

  methods: {
    appendNumber(num) {
      if (this.isKgMode) {
        if (this.quantityStr === "0" && num === 0) return;
        const newStr =
          this.quantityStr === "0"
            ? String(num)
            : this.quantityStr + String(num);
        const [intPart, decPart] = newStr.split(",");
        if (decPart !== undefined && decPart.length > 3) return;
        if (intPart.length > 6) return;
        this.quantityStr = newStr;
      } else {
        const nextValue = parseInt(this.quantityStr) * 10 + num;
        if (String(nextValue).length <= this.maxLength) {
          this.quantityStr = String(nextValue);
        }
      }
    },

    appendDecimal() {
      if (!this.isKgMode || this.quantityHasDecimal) return;
      this.quantityStr = this.quantityStr + ",";
    },

    resetQuantity() {
      this.quantityStr = "0";
    },

    deleteDigit() {
      if (this.quantityStr.length <= 1) {
        this.quantityStr = "0";
      } else {
        this.quantityStr = this.quantityStr.slice(0, -1);
      }
    },

    openDialog() {
      // Inicializar con la fecha/hora actual
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      this.createdOn = `${year}-${month}-${day}T${hours}:${minutes}`;
      this.$refs.dialog?.showModal();
    },

    closeDialog() {
      this.$refs.dialog?.close();
    },

    async confirmSave() {
      if (this.quantity === 0) {
        this.showResultDialog(false, "Cantidad inválida");
        return;
      }

      if (!this.format || !this.format.id) {
        this.showResultDialog(false, "Formato no seleccionado");
        return;
      }

      this.closeDialog();
      try {
        const payload = {
          farmland_id: this.farmland.id,
          format_id: this.format.id,
          units: this.quantity,
        };

        // Añadir fecha de creación si está disponible
        if (this.createdOn) {
          payload.created_on =
            this.createdOn.length === 16
              ? `${this.createdOn}:00`
              : this.createdOn;
        }

        // Añadir zone_id si existe
        if (this.zone && this.zone.id) {
          // Para fincas cooperativas, añadir modo y categoría
          if (this.farmland.farm_type === "COOPERATIVE") {
            payload.production_mode = this.modoObj?.code || "WEIGHT";
            payload.category = this.category;
          }

          payload.zone_id = this.zone.id;
        }

        const response = await this.$http.post(
          `${this.$hostname}/production/`,
          payload,
        );
        console.log("Datos guardados:", response.data);
        // Invalidar caché de la lista para que el próximo GET traiga datos frescos
        await cacheDelete(`${this.$hostname}/production/`);
        this.showResultDialog(
          true,
          "Producción guardada con éxito",
          response.data?.warning || null,
        );
      } catch (err) {
        console.error("Error al guardar producción:", err);
        this.showResultDialog(false, "Error al guardar producción");
      }
    },

    showResultDialog(success, message, warning = null) {
      this.resultDialog.success = success;
      this.resultDialog.message = message;
      this.resultDialog.warning = warning;
      this.$refs.resultDialog?.showModal();

      if (!success) {
        setTimeout(() => {
          this.$refs.resultDialog?.close();
        }, 1500);
      }
    },

    resetState() {
      this.quantityStr = "0";
    },

    acceptAndGoHome() {
      this.$refs.resultDialog?.close();
      this.resetState();
      this.$root.$emit("quantity");
    },

    goToMode() {
      this.$refs.resultDialog?.close();
      this.resetState();
      this.$root.$emit("goto-formats-mode");
    },

    goToCategory() {
      this.$refs.resultDialog?.close();
      this.resetState();
      this.$root.$emit("goto-formats-category");
    },
  },
};
</script>

<style scoped>
/* Filas de las últimas dos líneas usan flex para poder controlar anchos */
.quantity .row:nth-last-child(-n + 2) {
  display: flex !important;
  float: none !important;
}

.column.comma {
  width: 27% !important;
}

.column.comma.disabled {
  opacity: 0.35;
  pointer-events: none;
}

.column.zero {
  width: 27% !important;
}

.column.del {
  width: 27% !important;
}

.column.ac {
  width: 27% !important;
}

.column.ok {
  width: 57% !important;
}

.result-dialog-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  width: 100%;
}

.result-button {
  padding: 11px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  letter-spacing: 0.01em;
}

/* Botón principal: mismo estilo que ok-button de la app */
.accept-button {
  background: linear-gradient(135deg, #107631 0%, #98cf9c 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 6px rgba(16, 118, 49, 0.25);
}

.accept-button:hover {
  background: linear-gradient(135deg, #0d5e28 0%, #7ab87d 100%);
  box-shadow: 0 6px 10px rgba(16, 118, 49, 0.35);
  transform: translateY(-1px);
}

.accept-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(16, 118, 49, 0.2);
}

/* Botón secundario: contorno verde, fondo blanco */
.mode-button {
  background: white;
  color: #107631;
  border: 1.5px solid #107631;
}

.mode-button:hover {
  background: rgba(16, 118, 49, 0.06);
  border-color: #0d5e28;
  color: #0d5e28;
  transform: translateY(-1px);
}

.mode-button:active {
  transform: translateY(0);
}

/* Botón terciario: neutro gris, tono suave */
.category-button {
  background: rgba(160, 156, 156, 0.15);
  color: #444;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.category-button:hover {
  background: rgba(160, 156, 156, 0.28);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.category-button:active {
  transform: translateY(0);
}

.dialog-warning {
  font-size: 13px;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 8px 12px;
  margin: 4px 0 12px;
  text-align: center;
  line-height: 1.4;
}

.created-on-field {
  margin-top: 15px;
  padding-top: 10px;
  border-top: 1px solid #e5e5e5;
}

.created-on-field label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
}

.datetime-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
}

.datetime-input:focus {
  outline: none;
  border-color: #107631;
  box-shadow: 0 0 0 3px rgba(16, 118, 49, 0.1);
}
</style>
